import { NavLink, useLocation } from 'react-router-dom'
import {
  BarChart3,
  Shield,
  Users,
  Settings,
  FileText,
  AlertTriangle,
  TrendingUp,
  Database,
  Zap,
  CreditCard,
  Download,
  Building
} from 'lucide-react'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar'
import { useAuth } from '@/contexts/AuthContext'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { useRealTimeAlerts } from '@/hooks/useRealTimeAlerts'

const mainItems = [
  { title: 'Dashboard', url: '/dashboard', icon: BarChart3, badge: null },
  { title: 'Leads', url: '/leads', icon: Database, badge: 'leads' },
  { title: 'Rules', url: '/rules', icon: Zap, badge: null },
  { title: 'Alerts', url: '/alerts', icon: AlertTriangle, badge: 'alerts' },
  { title: 'Analytics', url: '/analytics', icon: TrendingUp, badge: null },
  { title: 'Export & Reports', url: '/exports', icon: Download, badge: null },
  { title: 'Patterns', url: '/patterns', icon: Shield, badge: null },
]

const managementItems = [
  { title: 'Team', url: '/team', icon: Users, roles: ['owner', 'admin'] },
  { title: 'Billing', url: '/billing', icon: CreditCard, roles: ['owner'] },
  { title: 'Settings', url: '/settings', icon: Settings, roles: ['owner', 'admin'] },
]

const supportItems = [
  { title: 'Documentation', url: '/docs', icon: FileText },
  { title: 'Support', url: '/support', icon: AlertTriangle },
]

export function AppSidebar() {
  const { state } = useSidebar()
  const location = useLocation()
  const { profile, organization, hasRole } = useAuth()
  const { unreadCount } = useRealTimeAlerts()
  
  const collapsed = state === 'collapsed'

  const getBadgeCount = (badgeType: string | null) => {
    switch (badgeType) {
      case 'leads':
        return unreadCount > 0 ? unreadCount : null
      case 'alerts':
        return unreadCount > 0 ? unreadCount : null
      default:
        return null
    }
  }

  const isActive = (url: string) => location.pathname === url

  const getNavClassName = (url: string) => {
    const active = isActive(url)
    return active 
      ? 'bg-primary/10 text-primary font-medium border-r-2 border-primary' 
      : 'hover:bg-muted/50 text-muted-foreground hover:text-foreground'
  }

  return (
    <Sidebar className={collapsed ? 'w-14' : 'w-64'} collapsible="icon">
      {/* Header with Logo & Org Info */}
      <SidebarHeader className="border-b p-4">
        {!collapsed ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Shield className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-sm truncate">
                  Form Entry Fraud Detection
                </h2>
                <p className="text-xs text-muted-foreground truncate">
                  {organization?.name || 'Loading...'}
                </p>
              </div>
            </div>
            
            {/* User Profile */}
            {profile && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                <Avatar className="w-6 h-6">
                  <AvatarImage src={profile.avatar_url || undefined} />
                  <AvatarFallback className="text-xs">
                    {profile.full_name?.charAt(0) || profile.email.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">
                    {profile.full_name || 'User'}
                  </p>
                  <Badge variant="secondary" className="text-xs h-4 px-1">
                    {profile.role}
                  </Badge>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-primary-foreground" />
            </div>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel>Main</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => {
                const badgeCount = getBadgeCount(item.badge)
                
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink to={item.url} className={getNavClassName(item.url)}>
                        <item.icon className="h-4 w-4" />
                        {!collapsed && (
                          <>
                            <span className="flex-1">{item.title}</span>
                            {badgeCount && (
                              <Badge className="ml-auto bg-primary text-primary-foreground h-5 px-2 text-xs">
                                {badgeCount > 99 ? '99+' : badgeCount}
                              </Badge>
                            )}
                          </>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Management */}
        <SidebarGroup>
          <SidebarGroupLabel>Management</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {managementItems.map((item) => {
                // Check if user has required role
                if (item.roles && !hasRole(item.roles)) {
                  return null
                }
                
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink to={item.url} className={getNavClassName(item.url)}>
                        <item.icon className="h-4 w-4" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Support */}
        <SidebarGroup>
          <SidebarGroupLabel>Support</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {supportItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} className={getNavClassName(item.url)}>
                      <item.icon className="h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer with organization info */}
      <SidebarFooter>
        {organization && !collapsed && (
          <div className="p-3 border-t bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-foreground truncate">
                  {organization.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  Organization
                </p>
              </div>
            </div>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  )
}